<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, useRoute } from 'vue-router';

import { NAV_ENTRIES, navTrail } from '@/shared/navigation';

import type { NavEntry } from '@/shared/navigation';

/**
 * «Previous / next screen» at the foot of every content page, in the order of
 * the table of contents. On a phone the menu is a drawer away; this lets the
 * reader go through a section like the chapters of a book, one thumb-tap
 * after the other.
 *
 * The caption over each link says where that screen is. «Таблица» alone does
 * not say whether the next page is the present or the future, so a screen of
 * a section with topics is captioned with its topic —
 * and with the section too only when the link leaves the current one: half a
 * phone screen has no room to repeat «Глаголы» on every page of «Глаголы».
 */
const route = useRoute();

const index = computed(() => NAV_ENTRIES.findIndex(({ link }) => route.path === link.to || route.path.startsWith(`${link.to}/`)));
const prev = computed(() => (index.value > 0 ? NAV_ENTRIES[index.value - 1] : null));
const next = computed(() => (index.value >= 0 && index.value < NAV_ENTRIES.length - 1 ? NAV_ENTRIES[index.value + 1] : null));

const captionOf = (entry: NavEntry): string => {
  // Inside the section the reader is already in, the topic alone is enough.
  const sameSection = NAV_ENTRIES[index.value]?.section === entry.section;

  return sameSection ? (entry.group?.title ?? entry.section.title) : navTrail(entry);
};
</script>

<template>
  <nav
    v-if="index >= 0"
    aria-label="Соседние страницы"
    class="mt-12 grid grid-cols-2 gap-3 border-t border-default pt-6"
  >
    <RouterLink
      v-if="prev"
      :to="prev.link.to"
      class="group flex min-h-16 flex-col justify-center gap-0.5 rounded-lg bg-elevated px-4 py-3 shadow-card ring-1 ring-default transition-colors hover:bg-muted"
    >
      <span class="flex items-start gap-1 text-xs text-muted">
        <UIcon
          name="i-lucide-arrow-left"
          class="mt-0.5 size-3.5 shrink-0"
        />
        {{ captionOf(prev) }}
      </span>
      <span class="text-sm font-semibold leading-snug text-highlighted">{{ prev.link.title }}</span>
    </RouterLink>
    <span v-else />

    <RouterLink
      v-if="next"
      :to="next.link.to"
      class="group flex min-h-16 flex-col items-end justify-center gap-0.5 rounded-lg bg-elevated px-4 py-3 text-right shadow-card ring-1 ring-default transition-colors hover:bg-muted"
    >
      <span class="flex items-start gap-1 text-xs text-muted">
        {{ captionOf(next) }}
        <UIcon
          name="i-lucide-arrow-right"
          class="mt-0.5 size-3.5 shrink-0"
        />
      </span>
      <span class="text-sm font-semibold leading-snug text-highlighted">{{ next.link.title }}</span>
    </RouterLink>
  </nav>
</template>
